// /v1/tokens — top tokens with optional sort + window controls.
//
// Discover taxonomy sorts (each ranked over the selected window):
//   top      → absolute-size composite (volume + txns + liquidity), liq ≥ $1k
//   trending → volume acceleration — heating up now, liq ≥ $1k
//   surge    → volume-confirmed price gainers, liq ≥ $1k
//   new      → youngest first (no liquidity floor)
// volume_24h / txns_24h stay valid as legacy raw-magnitude keys (default
// search board, direct callers). window: 1m | 5m | 15m | 30m | 1h | 24h
// (6h also accepted server-side). Backend validates the enum and 400s on
// unknown values. The legacy number-only signature is kept so existing call
// sites that pass just `limit` keep compiling unchanged.

import { rowKeyOf, tradeRowKey } from '../lib/discoverRowKey'
import { apiFetch } from './client'
import type {
  CompactWindowTradeEventV1,
  CompactWindowTradeEventV2,
  CompactWindowTradeTapeV3,
  ListEnvelope,
  TokenSummary,
  TradeSummary,
  WindowTradeEvent,
} from './types'

export type TokensSort = 'top' | 'trending' | 'surge' | 'new' | 'volume_24h' | 'txns_24h'
export type TokensWindow = '1m' | '5m' | '15m' | '30m' | '1h' | '24h'

/**
 * Server-side board filters. They run on the server because the browser only
 * holds the top N: a filter here could hide rows from the board but never
 * surface the token ranked 400th that the filter was looking for.
 *
 * Every bound is inclusive and optional. A value the server cannot prove
 * (unknown liquidity, unknown birth) fails a stated bound rather than passing
 * it — see backend/pkg/api/tokens/filters.go.
 */
export interface BoardFilterParams {
  minLiquidityUSD?: number
  maxLiquidityUSD?: number
  minVolumeNative?: number
  maxVolumeNative?: number
  minVolumeUSD?: number
  maxVolumeUSD?: number
  minMcapUSD?: number
  maxMcapUSD?: number
  minFdvUSD?: number
  maxFdvUSD?: number
  minTxns?: number
  maxTxns?: number
  minBuys?: number
  maxBuys?: number
  minSells?: number
  maxSells?: number
  minChangePct?: number
  maxChangePct?: number
  minBondingPct?: number
  maxBondingPct?: number
  /**
   * Cumulative FULL network fees in whole GRAM (TOKEN-NET-FEES). Labelled
   * "Fees (GRAM)" — a TON network-fee total, not the Solana
   * "Global Fees Paid (SOL)" concept. Rows whose fees were never observed are
   * excluded by any stated bound rather than counted as zero.
   */
  minFeesGram?: number
  maxFeesGram?: number
  minAgeSec?: number
  maxAgeSec?: number
  /** Any of 'x' | 'telegram' | 'website' | 'any'. */
  socials?: string[]
  /** Any of the six known launchpads; an unknown name is a 400, not silence. */
  launchpad?: string[]
  /**
   * Trading venue, collapsed to a family ('stonfi' covers v1 and v2). Disjoint
   * from `launchpad` in practice: a still-bonding token's venue IS its
   * launchpad, so a DEX selection means "already trading on an AMM".
   */
  dex?: string[]
  /** Ticker/name must contain ANY of these. */
  include?: string[]
  /** Ticker/name must contain NONE of these. Exclusion beats inclusion. */
  exclude?: string[]
}

export interface FetchTokensOpts {
  limit?: number
  sort?: TokensSort
  window?: TokensWindow
  includeMetadataIncomplete?: boolean
  completeWindow?: boolean
  afterTrade?: Pick<TradeSummary, 'pool_addr' | 'base_token' | 'quote_token' | 'ts' | 'tx_hash'>
  tradeDelta?: boolean
  minLiquidityUSD?: number
  /**
   * Ask for the server-ranked board: the top N of the requested axis instead
   * of the whole candidate universe. Such a response is deliberately NOT
   * candidate-set-complete — local time can no longer backfill an expired row,
   * so the caller must also consume board.enter / board.leave.
   */
  boardTopN?: number
  filters?: BoardFilterParams
  /** Filter-footer count: same membership, no tape, no rows. */
  countOnly?: boolean
}

export function fetchTokens(
  limitOrOpts: number | FetchTokensOpts = 50,
  opts?: RequestInit
): Promise<ListEnvelope<TokenSummary>> {
  const o: FetchTokensOpts = typeof limitOrOpts === 'number' ? { limit: limitOrOpts } : limitOrOpts
  if (o.tradeDelta && (!o.completeWindow || !o.afterTrade?.base_token)) {
    throw new Error('Discover trade delta requires a complete window and affected token master')
  }
  return apiFetch<ListEnvelope<TokenSummary>>(`/v1/tokens?${tokensQuery(o).toString()}`, opts)
    .then(expandTokenWindowEvents)
    .then((response) => validateCompleteWindow(response, o))
}

/** Pre-cut match count for the filter footer. Skips tape expand/validate so
 *  one bad event cannot blank the number the operator is staring at. */
/** The unranked New count is bounded by the request limit; probe at the
 *  server ceiling and let the UI mark the edge as "at least". */
export const NEW_COUNT_PROBE_LIMIT = 200

const WINDOW_SECONDS: Record<TokensWindow, number> = {
  '1m': 60,
  '5m': 300,
  '15m': 900,
  '30m': 1800,
  '1h': 3600,
  '24h': 86_400,
}

export function fetchBoardMatchCount(
  opts: Pick<FetchTokensOpts, 'sort' | 'window' | 'filters' | 'boardTopN'>,
  init?: RequestInit
): Promise<number> {
  const boardTopN = opts.boardTopN ?? 50
  // `new` is not a ranked axis: sending board_top_n is a 400 («ranked board
  // requires a ranked sort»), which is why the New tab's footer sat on the
  // rows.length fallback (always «50 tokens») while every ranked tab counted
  // honestly. The complete-window count_only itself works for new.
  const ranked = opts.sort !== 'new'
  // New means "born inside the selected window" (operator, 2026-08-20): the
  // count answers «сколько новых за 1h», not "how many newest exist at all".
  // Fold the window in as an age ceiling, never loosening a bound the user
  // already stated tighter.
  const winSec = opts.window ? WINDOW_SECONDS[opts.window] : undefined
  const filters =
    ranked || winSec == null
      ? opts.filters
      : {
          ...opts.filters,
          maxAgeSec: Math.min(opts.filters?.maxAgeSec ?? Number.POSITIVE_INFINITY, winSec),
        }
  return apiFetch<ListEnvelope<TokenSummary>>(
    `/v1/tokens?${tokensQuery({
      // New has no ranked cut, so the repo's own limit bounds the counted
      // universe — probe at the server ceiling so the number is honest up
      // to 200 (the UI renders "200+" at the edge).
      limit: ranked ? boardTopN : NEW_COUNT_PROBE_LIMIT,
      sort: opts.sort,
      window: opts.window,
      completeWindow: true,
      boardTopN: ranked ? boardTopN : undefined,
      filters,
      countOnly: true,
    }).toString()}`,
    init
  ).then((response) => {
    if (typeof response.filters_matched === 'number') return response.filters_matched
    // Live API still omitempty-skips 0. A mixed-platform AND that matches
    // nothing must read as 0, not "keep the previous 31".
    return response.items?.length ?? 0
  })
}

// Names match the server's query contract exactly (filter_params.go). Absent
// bounds are omitted rather than sent empty, because an empty value is a 400
// there — deliberately, so a filter can never silently do nothing.
const BOARD_FILTER_PARAM: Record<string, string> = {
  minLiquidityUSD: 'min_liq_usd',
  maxLiquidityUSD: 'max_liq_usd',
  minVolumeNative: 'min_volume',
  maxVolumeNative: 'max_volume',
  minVolumeUSD: 'min_volume_usd',
  maxVolumeUSD: 'max_volume_usd',
  minMcapUSD: 'min_mcap_usd',
  maxMcapUSD: 'max_mcap_usd',
  minFdvUSD: 'min_fdv_usd',
  maxFdvUSD: 'max_fdv_usd',
  minTxns: 'min_txns',
  maxTxns: 'max_txns',
  minBuys: 'min_buys',
  maxBuys: 'max_buys',
  minSells: 'min_sells',
  maxSells: 'max_sells',
  minChangePct: 'min_change_pct',
  maxChangePct: 'max_change_pct',
  minBondingPct: 'min_bonding_pct',
  maxBondingPct: 'max_bonding_pct',
  minFeesGram: 'min_fees_gram',
  maxFeesGram: 'max_fees_gram',
  minAgeSec: 'min_age_sec',
  maxAgeSec: 'max_age_sec',
}

const BOARD_FILTER_LIST_PARAM: Record<string, string> = {
  socials: 'socials',
  launchpad: 'launchpad',
  dex: 'dex',
  include: 'q',
  exclude: 'exclude',
}

export function appendBoardFilters(params: URLSearchParams, filters?: BoardFilterParams): void {
  if (!filters) return
  for (const [key, name] of Object.entries(BOARD_FILTER_PARAM)) {
    const value = (filters as Record<string, unknown>)[key]
    if (typeof value === 'number' && Number.isFinite(value)) params.set(name, String(value))
  }
  for (const [key, name] of Object.entries(BOARD_FILTER_LIST_PARAM)) {
    const value = (filters as Record<string, unknown>)[key]
    if (!Array.isArray(value)) continue
    const terms = value.map((v) => String(v).trim()).filter(Boolean)
    if (terms.length > 0) params.set(name, terms.join(','))
  }
}

function tokensQuery(o: FetchTokensOpts): URLSearchParams {
  const params = new URLSearchParams()
  params.set('limit', String(o.limit ?? 50))
  if (o.sort) params.set('sort', o.sort)
  if (o.window) params.set('window', o.window)
  if (o.includeMetadataIncomplete) params.set('include_metadata_incomplete', '1')
  if (o.completeWindow) params.set('complete_window', '1')
  if (o.afterTrade?.pool_addr && o.afterTrade.tx_hash) {
    params.set('after_trade_pool', o.afterTrade.pool_addr)
    params.set('after_trade_ts', String(Math.trunc(o.afterTrade.ts)))
    params.set('after_trade_tx', o.afterTrade.tx_hash)
    if (o.afterTrade.base_token) params.set('after_trade_master', o.afterTrade.base_token)
    const row = tradeRowKey(o.afterTrade)
    if (row) params.set('after_trade_row', row)
  }
  if (o.tradeDelta) params.set('trade_delta', '1')
  if (o.boardTopN != null) params.set('board_top_n', String(o.boardTopN))
  if (o.countOnly) params.set('count_only', '1')
  appendBoardFilters(params, o.filters)
  if (o.minLiquidityUSD != null) params.set('min_liquidity_usd', String(o.minLiquidityUSD))
  // Every Discover surface reads the columnar tape; the board's enter events
  // are compacted the same way server-side so the two shapes match.
  params.set('surface_wire', '3')
  return params
}

function validateCompleteWindow(
  response: ListEnvelope<TokenSummary>,
  request: FetchTokensOpts
): ListEnvelope<TokenSummary> {
  if (
    request.minLiquidityUSD != null &&
    response.min_liquidity_usd_applied !== request.minLiquidityUSD
  ) {
    throw new Error('Token snapshot did not apply the requested liquidity floor')
  }
  if (!request.completeWindow) return response
  validateWindowShape(response, request)
  // Filters narrow the universe but do not page it: an uncut filtered response
  // still carries every matching row, so completeness is still required and
  // still true. What must ALSO hold is that the server really applied them —
  // a silently ignored filter reads as "all the tokens that match" when the
  // board is really "all the tokens".
  if (request.filters && response.filters_applied !== true) {
    throw new Error('Discover response did not apply the requested filters')
  }
  if (request.tradeDelta) validateTradeDelta(response, request)
  else if (request.boardTopN != null) validateRankedBoard(response, request)
  else if (response.candidate_set_complete !== true) {
    throw new Error('Discover rolling-window candidate set is incomplete')
  }
  return response
}

function validateWindowShape(response: ListEnvelope<TokenSummary>, request: FetchTokensOpts): void {
  // WINDOW_SECONDS is a Record over the TokensWindow union, so it is exhaustive
  // at compile time. The switch this replaced had a `default: 86_400` arm: any
  // window not spelled out fell through to 24h silently, and the server already
  // accepts a window the switch did not carry ('6h' -> 21600 on the live API),
  // so adding it to the union would have broken every Discover request at
  // runtime instead of at build time. One mapping, checked by the compiler.
  const expectedSeconds = request.window ? WINDOW_SECONDS[request.window] : WINDOW_SECONDS['24h']
  if (response.window_seconds !== expectedSeconds) {
    throw new Error('Discover rolling-window snapshot uses the wrong time window')
  }
  if (response.items.some((item) => item.window_events == null)) {
    throw new Error('Discover rolling-window snapshot is missing its exact event tape')
  }
  if (
    isRankedDiscoverSort(request.sort) &&
    typeof response.discover_liquidity_floor_applied !== 'boolean'
  ) {
    throw new Error('Discover rolling-window snapshot is missing its liquidity-floor contract')
  }
}

// A ranked board is a cut, and every part of that has to be provable from the
// response. The dangerous failure is silent: a board that quietly carries fewer
// rows, or was cut for a different axis than the one being rendered, looks like
// a working board with tokens mysteriously missing.
function validateRankedBoard(response: ListEnvelope<TokenSummary>, request: FetchTokensOpts): void {
  if (response.snapshot_kind !== 'ranked_board') {
    throw new Error('Discover board request did not return a ranked board')
  }
  if (response.candidate_set_complete === true) {
    throw new Error('Discover ranked board falsely claims a complete candidate set')
  }
  if (response.board_top_n !== request.boardTopN) {
    throw new Error('Discover ranked board was cut to a different size than requested')
  }
  // board_sorts names the ONLY axes this response can be ranked by. Rendering
  // an axis it was not cut for would show a board built for a different tab.
  const sort = request.sort ?? 'top'
  if (!response.board_sorts?.includes(sort)) {
    throw new Error('Discover ranked board was not cut for the requested sort')
  }
  if (response.items.length > (request.boardTopN ?? 0)) {
    throw new Error('Discover ranked board returned more rows than it was cut to')
  }
}

function validateTradeDelta(response: ListEnvelope<TokenSummary>, request: FetchTokensOpts): void {
  const trade = request.afterTrade
  if (!trade || response.snapshot_kind !== 'trade_delta') {
    throw new Error('Discover trade delta did not cover the requested trade barrier')
  }
  if (response.candidate_set_complete === true) {
    throw new Error('Discover trade delta falsely claims a complete candidate set')
  }
  const disposition = response.requested_trade_disposition
  if (disposition !== 'included' && disposition !== 'excluded') {
    throw new Error('Discover trade delta is missing its authoritative disposition')
  }
  if (response.requested_trade_master !== trade.base_token) {
    throw new Error('Discover trade delta targets the wrong token master')
  }
  if (
    response.requested_trade_row_key != null &&
    response.requested_trade_row_key !== tradeRowKey(trade)
  ) {
    throw new Error('Discover trade delta targets the wrong row')
  }
  const action = response.requested_trade_action
  if (action !== 'upsert' && action !== 'remove' && action !== 'noop') {
    throw new Error('Discover trade delta is missing its authoritative membership action')
  }
  validateTradeDeltaItems(response, trade, action)
  if (disposition === 'included' && action !== 'upsert') {
    throw new Error('Discover included trade delta is missing its affected row')
  }
  if (
    disposition === 'included' &&
    !(response.market_included_pending ?? []).includes(pendingTradeKey(trade))
  ) {
    throw new Error('Discover included trade delta is missing exact barrier acknowledgement')
  }
}

function validateTradeDeltaItems(
  response: ListEnvelope<TokenSummary>,
  trade: Pick<TradeSummary, 'base_token' | 'pool_addr' | 'quote_token'>,
  action: 'upsert' | 'remove' | 'noop'
): void {
  if (action === 'upsert') {
    const item = response.items[0]
    if (
      response.items.length !== 1 ||
      !item ||
      item.jetton_master !== trade.base_token ||
      rowKeyOf(item) !== tradeRowKey(trade)
    ) {
      throw new Error('Discover trade delta does not contain exactly one affected row')
    }
    return
  }
  if (response.items.length !== 0) {
    throw new Error('Discover non-upsert trade delta contains candidate rows')
  }
}

function isRankedDiscoverSort(sort: TokensSort | undefined): boolean {
  return sort === 'top' || sort === 'trending' || sort === 'surge'
}

function pendingTradeKey(trade: Pick<TradeSummary, 'pool_addr' | 'ts' | 'tx_hash'>): string {
  const pool = trade.pool_addr ?? ''
  const nanos = BigInt(Math.trunc(trade.ts)) * 1_000_000_000n
  return `${pool.length}:${pool}${nanos}:${trade.tx_hash.length}:${trade.tx_hash}`
}

function expandTokenWindowEvents(response: ListEnvelope<TokenSummary>): ListEnvelope<TokenSummary> {
  return {
    ...response,
    items: response.items.map((item) => {
      try {
        return expandOneTokenWindowEvents(item)
      } catch {
        // One bad tape must not blank the whole Discover board. The row's
        // summary fields stay; local window projection for that token is dropped.
        const {
          window_events_compact: _legacy,
          window_events_compact_v2: _v2,
          window_events_compact_v3: _v3,
          ...surfaceItem
        } = item
        return { ...surfaceItem, window_events: [] }
      }
    }),
  }
}

function expandOneTokenWindowEvents(item: TokenSummary): TokenSummary {
  const compactV3 = item.window_events_compact_v3
  const compactV2 = item.window_events_compact_v2
  const compactV1 = item.window_events_compact
  if (compactV3 == null && compactV2 == null && compactV1 == null) return item
  const {
    window_events_compact: _legacy,
    window_events_compact_v2: _v2,
    window_events_compact_v3: _v3,
    ...surfaceItem
  } = item
  return {
    ...surfaceItem,
    window_events:
      (compactV3 != null
        ? expandWindowEventsV3(compactV3, item)
        : compactV2?.map((event, index) =>
            expandWindowEventV2(event, item, index === compactV2.length - 1)
          )) ?? compactV1?.map((event) => expandWindowEventV1(event, item)),
  }
}

function expandWindowEventsV3(
  tape: CompactWindowTradeTapeV3,
  item: TokenSummary
): WindowTradeEvent[] {
  const [
    baseTS,
    baseLT,
    tsDeltas,
    ltDeltas,
    poolCodes,
    poolDictionary,
    sides,
    quote,
    native,
    rates,
    refs,
    direct,
  ] = tape
  const length = assertV3TapeShape(tsDeltas, ltDeltas, poolCodes, sides, quote, native, refs)
  const pools = poolDictionary ?? []
  const cursor = { ts: 0, lt: 0, ltExact: 0n }
  return tsDeltas.map((_, index) => {
    const event = readV3Event(
      { baseTS, baseLT, tsDeltas, ltDeltas, poolCodes, sides, quote, native, rates, refs, direct },
      index,
      cursor
    )
    const poolAddr = v3PoolAddr(event.poolCode, pools, item)
    return {
      ts: cursor.ts,
      lt: cursor.lt,
      lt_key: event.ltKey,
      tx_hash: `seed:${event.ltKey}:${event.sideCode}:${event.quoteNative}:${event.nativePrice}`,
      side: v3Side(event.sideCode),
      pool_addr: poolAddr,
      base_token: item.jetton_master,
      quote_native: event.quoteNative,
      quote_usd: quoteUSDFor(event.quoteNative, event.nativePrice, event.usdPrice),
      native_price: event.nativePrice,
      usd_price: event.usdPrice,
      mcap_usd: index === length - 1 ? item.mcap_usd : null,
      fdv_usd: index === length - 1 ? item.fdv_usd : null,
      liquidity_usd: index === length - 1 ? item.liquidity_usd : null,
    }
  })
}

function assertV3TapeShape(
  tsDeltas: number[],
  ltDeltas: Array<string | number>,
  poolCodes: number[] | null,
  sides: string,
  quote: number[],
  native: number[],
  refs: number[]
): number {
  const length = tsDeltas.length
  if (
    ltDeltas.length !== length ||
    (poolCodes != null && poolCodes.length !== length) ||
    sides.length !== length ||
    quote.length !== length ||
    native.length !== length ||
    refs.length !== length
  ) {
    throw new Error('Discover v3 tape has mismatched column lengths')
  }
  return length
}

function readV3Event(
  tape: {
    baseTS: number
    baseLT: string | number
    tsDeltas: number[]
    ltDeltas: Array<string | number>
    poolCodes: number[] | null
    sides: string
    quote: number[]
    native: number[]
    rates: number[]
    refs: number[]
    direct: number[]
  },
  index: number,
  cursor: { ts: number; lt: number; ltExact: bigint }
) {
  const tsDelta = requiredV3SafeInteger(tape.tsDeltas[index])
  const ltDelta = requiredV3LT(tape.ltDeltas[index])
  const poolCode = tape.poolCodes == null ? 0 : requiredV3Integer(tape.poolCodes[index])
  const quoteNative = requiredV3Finite(tape.quote[index])
  const nativePrice = requiredV3Finite(tape.native[index])
  const usdPrice = v3USDPrice(nativePrice, tape.refs[index], tape.rates, tape.direct)
  const nextTS = index === 0 ? requiredV3SafeInteger(tape.baseTS) + tsDelta : cursor.ts + tsDelta
  const nextLT = index === 0 ? requiredV3LT(tape.baseLT) + ltDelta : cursor.ltExact + ltDelta
  // Equal LT is a real chain fact (two fills in one tx). Reject only a
  // cursor that walks backwards — that is the corrupt wire, not a tie.
  if (nextTS < cursor.ts || nextLT < cursor.ltExact) {
    throw new Error('Discover v3 tape has a non-monotonic cursor')
  }
  cursor.ts = nextTS
  cursor.ltExact = nextLT
  // `lt` remains for legacy display consumers; every ordering/identity path
  // uses lt_key, never this potentially rounded presentation number.
  cursor.lt = Number(nextLT)
  return {
    poolCode,
    sideCode: tape.sides[index],
    quoteNative,
    nativePrice,
    usdPrice,
    ltKey: nextLT.toString(),
  }
}

function requiredV3LT(value: string | number | undefined): bigint {
  if (typeof value === 'number') {
    if (!Number.isSafeInteger(value) || value < 0) {
      throw new Error('Discover v3 tape contains an invalid LT')
    }
    return BigInt(value)
  }
  if (typeof value !== 'string' || !/^(0|[1-9]\d*)$/.test(value)) {
    throw new Error('Discover v3 tape contains an invalid LT')
  }
  return BigInt(value)
}

function requiredV3SafeInteger(value: number | undefined): number {
  if (value == null || !Number.isSafeInteger(value)) {
    throw new Error('Discover v3 tape contains an invalid value')
  }
  return value
}

function requiredV3Finite(value: number | undefined): number {
  if (value == null || !Number.isFinite(value)) {
    throw new Error('Discover v3 tape contains an invalid value')
  }
  return value
}

function requiredV3Integer(value: number | undefined): number {
  if (value == null || !Number.isInteger(value)) {
    throw new Error('Discover v3 tape contains an invalid value')
  }
  return value
}

function v3USDPrice(
  nativePrice: number,
  ref: number | undefined,
  rates: number[],
  direct: number[]
): number | null {
  const checkedRef = requiredV3Integer(ref)
  if (checkedRef === -1) return null
  if (checkedRef >= 0) return nativePrice * requiredV3Finite(rates[checkedRef])
  return requiredV3Finite(direct[-2 - checkedRef])
}

function v3PoolAddr(poolCode: number, pools: string[], item: TokenSummary): string {
  const poolAddr =
    poolCode === 0
      ? (item.primary_pool_addr ?? item.jetton_master)
      : poolCode === 1
        ? item.jetton_master
        : pools[poolCode - 2]
  if (!poolAddr) throw new Error('Discover v3 tape contains an unknown pool discriminator')
  return poolAddr
}

function v3Side(sideCode: string | undefined): 'buy' | 'sell' | 'unknown' {
  if (sideCode === 'b') return 'buy'
  if (sideCode === 's') return 'sell'
  if (sideCode === 'u') return 'unknown'
  throw new Error('Discover v3 tape contains an unknown side discriminator')
}

function quoteUSDFor(
  quoteNative: number,
  nativePrice: number,
  usdPrice: number | null
): number | null {
  return finitePositive(usdPrice) && finitePositive(nativePrice)
    ? quoteNative * ((usdPrice as number) / nativePrice)
    : null
}

function expandWindowEventV2(
  event: CompactWindowTradeEventV2,
  item: TokenSummary,
  newest: boolean
): WindowTradeEvent {
  const [ts, lt, pool, side, quoteNative, nativePrice, usdPrice] = event
  const quoteUSD =
    finitePositive(usdPrice) && finitePositive(nativePrice)
      ? quoteNative * ((usdPrice as number) / nativePrice)
      : null
  return {
    ts,
    lt,
    // The immutable REST seed is cursor-covered before buffered WS replay.
    // A deterministic local identity keeps equal seed events distinct without
    // repeating the 44-byte transaction hash on every wire tuple.
    tx_hash: `seed:${lt}:${side}:${quoteNative}:${nativePrice}`,
    side: side === 1 ? 'buy' : side === -1 ? 'sell' : 'unknown',
    pool_addr:
      pool === 0
        ? (item.primary_pool_addr ?? item.jetton_master)
        : pool === 1
          ? item.jetton_master
          : pool,
    base_token: item.jetton_master,
    quote_native: quoteNative,
    quote_usd: quoteUSD,
    native_price: nativePrice,
    usd_price: usdPrice,
    mcap_usd: newest ? item.mcap_usd : null,
    fdv_usd: newest ? item.fdv_usd : null,
    liquidity_usd: newest ? item.liquidity_usd : null,
  }
}

function expandWindowEventV1(
  event: CompactWindowTradeEventV1,
  item: TokenSummary
): WindowTradeEvent {
  const [
    ts,
    lt,
    txHash,
    pool,
    side,
    quoteNative,
    quoteUSD,
    nativePrice,
    usdPrice,
    mcapUSD,
    fdvUSD,
    liquidityUSD,
  ] = event
  return {
    ts,
    lt,
    tx_hash: txHash,
    side: side === 1 ? 'buy' : side === -1 ? 'sell' : 'unknown',
    pool_addr:
      pool === 0
        ? (item.primary_pool_addr ?? item.jetton_master)
        : pool === 1
          ? item.jetton_master
          : pool,
    base_token: item.jetton_master,
    quote_native: quoteNative,
    quote_usd: quoteUSD,
    native_price: nativePrice,
    usd_price: usdPrice,
    mcap_usd: mcapUSD,
    fdv_usd: fdvUSD,
    liquidity_usd: liquidityUSD,
  }
}

function finitePositive(value: number | null | undefined): boolean {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
}
